/**
 * Replay engine (WS-17b) — game-kind-generic by design.
 *
 * The insight this file leans on: **the move log already IS the replay.** Every game kind
 * in this module stores an append-only `Y.Array` of moves and a pure `derive(movesPrefix)`
 * function that turns *any* prefix of that array into a state (`deriveBoard`/`deriveConnect4`
 * for Connect-4 today; a future kind — battleship shots, a Word Duel guess sequence — needs
 * nothing more than its own move type and its own `derive` function shaped the same way).
 * `replayStates` just calls `derive` once per prefix length and hands back every intermediate
 * state as a `ReplayFrame`, so a scrubber/player UI never needs its own replay logic, only an
 * index into the array this returns.
 *
 * Deliberately dumb: no memoization beyond what the caller wants, no game-kind-specific
 * knowledge, no notion of "current" state (frame `frames.length - 1` already *is* the same
 * state a live, non-replaying view would show — see `useGameMoves`). Kept pure so it's
 * testable without touching Yjs, React, or a clock.
 */
/**
 * Replays `moves` prefix-by-prefix through `derive`, returning one frame per prefix length
 * from `0` (nothing applied) through `moves.length` (everything applied — the same state a
 * live view shows). `derive` is the one thing a game kind supplies: Connect-4 plugs in
 * `(prefix) => deriveConnect4(prefix, playerIds)`; a future kind supplies its own equivalent
 * over its own move/state types and gets scrubbing, playback, and archive replay for free.
 *
 * `O(n)` calls to `derive`, each doing whatever work `derive` does on its prefix (for
 * Connect-4, `O(prefix length)`) — quadratic overall, which is fine at the length these
 * matches run (Connect-4 tops out at 42 moves) and keeps `derive` itself dead simple: it
 * never has to reason about incremental state, only "given these moves, what's true now."
 */
export function replayStates(moves, derive) {
    const frames = [{ moveCount: 0, move: null, state: derive([]) }];
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        const frame = {
            moveCount: i + 1,
            move,
            state: derive(moves.slice(0, i + 1)),
        };
        if (move.at)
            frame.at = move.at;
        frames.push(frame);
    }
    return frames;
}
/** Clamps a frame index into `[0, frames.length - 1]` — the shared bounds-check every
 * scrubber/stepper UI needs (step buttons, a dragged slider, a jump-to-end tap). */
export function clampFrameIndex(frames, index) {
    const max = Math.max(0, frames.length - 1);
    return Math.min(max, Math.max(0, index));
}
/**
 * "played over 3 days" / "played in one sitting" — a fact about pacing, derived from
 * however many of `moves` happen to carry an `at` stamp. Returns `undefined` (never a
 * guess) when there aren't at least two stamped moves to compare — the common case for
 * legacy games recorded before WS-17b, whose moves have no `at` at all.
 */
export function pacingLabel(moves) {
    const stamps = moves.map((m) => m.at).filter((at) => Boolean(at));
    if (stamps.length < 2)
        return undefined;
    const first = Date.parse(stamps[0]);
    const last = Date.parse(stamps[stamps.length - 1]);
    if (!Number.isFinite(first) || !Number.isFinite(last) || last < first)
        return undefined;
    const days = Math.floor((last - first) / (24 * 60 * 60 * 1000));
    if (days <= 0)
        return 'played in one sitting';
    if (days === 1)
        return 'played over 2 days';
    return `played over ${days + 1} days`;
}
//# sourceMappingURL=replay.js.map